import { z } from "zod/v4"
import { api } from "../core"
import { useAccessContext } from "../component/guard/AccessGuard"
import { useToast } from "popopo-lib-front/hook"
import { useZodForm } from "popopo-lib-front/zod-form"
import {
  type UserId,
  type SpaceKey,
  type LiveId,
  type StoryId,
  type LiveCommentId,
  type SpaceMessageId,
  type SelectionId,
  type SelectionParticipantId,
  type VoiceId,
  type VoiceReplyId,
  ViolationReports,
  ViolationReportReasonSchema,
  ViolationReportTargetKindSchema,
} from "popopo-msg"
import { assertNever } from "popopo-lib-common"
import type { components } from "popopo-msg/api/schema"

type ApiViolationReportTarget = components["schemas"]["ViolationReportTarget"]

const formSchema = z.object({
  targetKind: ViolationReportTargetKindSchema,
  reason: ViolationReportReasonSchema,
  detail: z.string().max(ViolationReports.DETAIL_MAX_LENGTH).optional(),
  userId: z.string().optional(),
  spaceKey: z.string().optional(),
  liveId: z.string().optional(),
  storyId: z.string().optional(),
  commentId: z.string().optional(),
  messageId: z.string().optional(),
  selectionId: z.string().optional(),
  participantId: z.string().optional(),
  voiceId: z.string().optional(),
  voiceReplyId: z.string().optional(),
})

type ViolationReportFormData = z.infer<typeof formSchema>

function buildTarget(data: ViolationReportFormData): ApiViolationReportTarget | null {
  switch (data.targetKind) {
    case "user":
      if (!data.userId) return null
      return { kind: "user", userId: data.userId as UserId }
    case "user-profile":
      if (!data.userId) return null
      return { kind: "user-profile", userId: data.userId as UserId }
    case "space":
      if (!data.spaceKey) return null
      return { kind: "space", spaceKey: data.spaceKey as SpaceKey }
    case "story":
      if (!data.userId || !data.storyId) return null
      return { kind: "story", userId: data.userId as UserId, storyId: data.storyId as StoryId }
    case "live-comment":
      if (!data.spaceKey || !data.liveId || !data.commentId) return null
      return {
        kind: "live-comment",
        spaceKey: data.spaceKey as SpaceKey,
        liveId: data.liveId as LiveId,
        commentId: data.commentId as LiveCommentId,
      }
    case "space-message":
      if (!data.spaceKey || !data.messageId) return null
      return {
        kind: "space-message",
        spaceKey: data.spaceKey as SpaceKey,
        messageId: data.messageId as SpaceMessageId,
      }
    case "selection-participant":
      if (!data.spaceKey || !data.liveId || !data.selectionId || !data.participantId) return null
      return {
        kind: "selection-participant",
        spaceKey: data.spaceKey as SpaceKey,
        liveId: data.liveId as LiveId,
        selectionId: data.selectionId as SelectionId,
        participantId: data.participantId as SelectionParticipantId,
      }
    case "live":
      if (!data.spaceKey || !data.liveId) return null
      return {
        kind: "live",
        spaceKey: data.spaceKey as SpaceKey,
        liveId: data.liveId as LiveId,
      }
    case "voice":
      if (!data.userId || !data.voiceId) return null
      return {
        kind: "voice",
        userId: data.userId as UserId,
        voiceId: data.voiceId as VoiceId,
      }
    case "voice-reply":
      if (!data.userId || !data.voiceId || !data.voiceReplyId) return null
      return {
        kind: "voice-reply",
        userId: data.userId as UserId,
        voiceId: data.voiceId as VoiceId,
        voiceReplyId: data.voiceReplyId as VoiceReplyId,
      }
    default:
      return assertNever(data.targetKind)
  }
}

export const ViolationReport = () => {
  const { credential } = useAccessContext()
  const toast = useToast()

  const form = useZodForm({
    schema: formSchema,
    mode: "onChange",
    defaultValues: {
      targetKind: "user",
      reason: "other",
      detail: "",
      userId: "",
      spaceKey: "",
      liveId: "",
      storyId: "",
      commentId: "",
      messageId: "",
      selectionId: "",
      participantId: "",
    },
  })

  const targetKind = form.watch("targetKind")

  const onSubmit = async (data: ViolationReportFormData) => {
    try {
      const idToken = await credential.user.getIdToken()
      const target = buildTarget(data)

      if (!target) {
        form.setError("root", { message: "通報対象の情報が不足しています。必須フィールドを入力してください。" })
        return
      }

      const response = await api.POST("/api/v2/violation-reports", {
        headers: {
          authorization: `Bearer ${idToken}`,
        },
        body: {
          target,
          reason: data.reason,
          detail: data.detail,
        },
      })

      if (response.error) {
        toast.add({
          type: "danger",
          title: "通報",
          message: `通報の送信に失敗しました: ${JSON.stringify(response.error)}`,
        })
        return
      }

      toast.add({
        type: "success",
        title: "通報",
        message: "通報を送信しました",
      })
      form.reset()
    } catch (e) {
      console.error(e)
      toast.add({
        type: "danger",
        title: "通報",
        message: "通報の送信中にエラーが発生しました",
      })
    }
  }

  const needsUserId = ["user", "user-profile", "story", "voice", "voice-reply"].includes(targetKind)
  const needsSpaceKey = ["space", "live-comment", "space-message", "selection-participant", "live"].includes(targetKind)
  const needsLiveId = ["live-comment", "selection-participant", "live"].includes(targetKind)
  const needsStoryId = targetKind === "story"
  const needsCommentId = targetKind === "live-comment"
  const needsMessageId = targetKind === "space-message"
  const needsSelectionId = targetKind === "selection-participant"
  const needsParticipantId = targetKind === "selection-participant"
  const needsVoiceId = ["voice", "voice-reply"].includes(targetKind)
  const needsVoiceReplyId = targetKind === "voice-reply"
  const { isSubmitting, isValid, errors } = form.formState

  return (
    <div className="container py-4">
      <h2 className="mb-4">通報テスト</h2>

      <form className="d-flex flex-column gap-3" onSubmit={form.handleSubmit(onSubmit)} style={{ maxWidth: "600px" }}>
        {errors.root && (
          <div className="alert alert-danger" role="alert">
            {errors.root.message}
          </div>
        )}

        <div className="form-floating">
          <select className="form-control" {...form.register("targetKind")}>
            {ViolationReports.targetKinds.map((kind) => (
              <option key={kind.value} value={kind.value}>
                {kind.description}
              </option>
            ))}
          </select>
          <label>通報対象の種別</label>
        </div>

        {needsUserId && (
          <div className="form-floating">
            <input
              className={`form-control ${errors.userId ? "is-invalid" : ""}`}
              {...form.register("userId")}
              disabled={isSubmitting}
              placeholder="ユーザーID"
            />
            <label>ユーザーID</label>
            {errors.userId && (
              <div className="invalid-feedback">{errors.userId.message}</div>
            )}
          </div>
        )}

        {needsSpaceKey && (
          <div className="form-floating">
            <input
              className={`form-control ${errors.spaceKey ? "is-invalid" : ""}`}
              {...form.register("spaceKey")}
              disabled={isSubmitting}
              placeholder="スペースキー"
            />
            <label>スペースキー</label>
            {errors.spaceKey && (
              <div className="invalid-feedback">{errors.spaceKey.message}</div>
            )}
          </div>
        )}

        {needsLiveId && (
          <div className="form-floating">
            <input
              className={`form-control ${errors.liveId ? "is-invalid" : ""}`}
              {...form.register("liveId")}
              disabled={isSubmitting}
              placeholder="ライブID"
            />
            <label>ライブID</label>
            {errors.liveId && (
              <div className="invalid-feedback">{errors.liveId.message}</div>
            )}
          </div>
        )}

        {needsStoryId && (
          <div className="form-floating">
            <input
              className={`form-control ${errors.storyId ? "is-invalid" : ""}`}
              {...form.register("storyId")}
              disabled={isSubmitting}
              placeholder="ストーリーID"
            />
            <label>ストーリーID</label>
            {errors.storyId && (
              <div className="invalid-feedback">{errors.storyId.message}</div>
            )}
          </div>
        )}

        {needsCommentId && (
          <div className="form-floating">
            <input
              className={`form-control ${errors.commentId ? "is-invalid" : ""}`}
              {...form.register("commentId")}
              disabled={isSubmitting}
              placeholder="コメントID"
            />
            <label>コメントID</label>
            {errors.commentId && (
              <div className="invalid-feedback">{errors.commentId.message}</div>
            )}
          </div>
        )}

        {needsMessageId && (
          <div className="form-floating">
            <input
              className={`form-control ${errors.messageId ? "is-invalid" : ""}`}
              {...form.register("messageId")}
              disabled={isSubmitting}
              placeholder="メッセージID"
            />
            <label>メッセージID</label>
            {errors.messageId && (
              <div className="invalid-feedback">{errors.messageId.message}</div>
            )}
          </div>
        )}

        {needsSelectionId && (
          <div className="form-floating">
            <input
              className={`form-control ${errors.selectionId ? "is-invalid" : ""}`}
              {...form.register("selectionId")}
              disabled={isSubmitting}
              placeholder="セレクションID"
            />
            <label>セレクションID</label>
            {errors.selectionId && (
              <div className="invalid-feedback">{errors.selectionId.message}</div>
            )}
          </div>
        )}

        {needsParticipantId && (
          <div className="form-floating">
            <input
              className={`form-control ${errors.participantId ? "is-invalid" : ""}`}
              {...form.register("participantId")}
              disabled={isSubmitting}
              placeholder="参加者ID"
            />
            <label>参加者ID</label>
            {errors.participantId && (
              <div className="invalid-feedback">{errors.participantId.message}</div>
            )}
          </div>
        )}

        {needsVoiceId && (
          <div className="form-floating">
            <input
              className={`form-control ${errors.voiceId ? "is-invalid" : ""}`}
              {...form.register("voiceId")}
              disabled={isSubmitting}
              placeholder="ボイスID"
            />
            <label>ボイスID</label>
            {errors.voiceId && (
              <div className="invalid-feedback">{errors.voiceId.message}</div>
            )}
          </div>
        )}

        {needsVoiceReplyId && (
          <div className="form-floating">
            <input
              className={`form-control ${errors.voiceReplyId ? "is-invalid" : ""}`}
              {...form.register("voiceReplyId")}
              disabled={isSubmitting}
              placeholder="ボイス返信ID"
            />
            <label>ボイス返信ID</label>
            {errors.voiceReplyId && (
              <div className="invalid-feedback">{errors.voiceReplyId.message}</div>
            )}
          </div>
        )}

        <div className="form-floating">
          <select className="form-control" {...form.register("reason")}>
            {ViolationReports.reasons.map((reason) => (
              <option key={reason.value} value={reason.value}>
                {reason.description}
              </option>
            ))}
          </select>
          <label>通報の理由</label>
        </div>

        <div className="form-floating">
          <input
            className="form-control"
            {...form.register("detail")}
            disabled={isSubmitting}
            placeholder="詳細（任意）"
          />
          <label>詳細（任意）</label>
        </div>

        <button
          type="submit"
          className="btn btn-primary"
          disabled={isSubmitting || !isValid}
        >
          {isSubmitting ? "送信中..." : "通報を送信"}
        </button>
      </form>
    </div>
  )
}
